// -----------------------------------------------------------------------------
// src/Components/BookingForm.js   (FULL UPDATED)
// -----------------------------------------------------------------------------
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Backdrop,
  Box,
  Button,
  CardMedia,
  CircularProgress,
  Divider,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from 'react-query';
import useAlertDialog from './useAlertDialog';
import useAuth from '../Hooks/use-auth';
import {
  getPlatformAndPromotion,
  parseLocalFloat,
  platformRatesListFromCreator,
} from '../Utils/constants';
import API from '../API';
import AlertDialog from './AlertDialog';
import { RateField } from './RateField';
import defaultPfp from './globalAssets/ppfLogo.png';

/**
 * BookingForm
 *
 * @param {object}  creator       creator object
 * @param {func}    onClose       close callback
 * @param {boolean} isCelebrity   ← NEW: if true, leave amount blank & skip auto-fill
 */
export default function BookingForm({ creator, onClose, isCelebrity = false }) {
  const navigate                         = useNavigate();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const { token: currentToken } = useAuth();
  /* ──────────────── LOCAL STATE ──────────────── */

  const [deliverableData, setDeliverableData] = useState({
    deliverableType: 'offer',
    amount: isCelebrity ? '' : 50,        // ← leave blank for celebrity tier
    notes: '',
    revision: 0,
    script: false,
    email: '',
  });

  const [partnershipData, setPartnershipData] = useState({
    partnershipName: '',
    details: '',
    selectedPromotion: '',
    promotionType: '',
    promotionPlatform: '',
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [offerWarning, setOfferWarning] = useState('');

  const promotions = useMemo(() => platformRatesListFromCreator(creator), [creator]);

  /* ──────────────── EFFECTS ──────────────── */

  // 1. Sensible default promotion (but DON'T auto-fill amount for celebs)
  useEffect(() => {
    if (!promotions.length) return;
    const defaultPromo         = promotions[promotions[1] ? 1 : 0];
    const { rateKey }          = defaultPromo;
    const promoMeta            = getPlatformAndPromotion(rateKey);
    const recommended          = creator[rateKey] ?? 50;

    setPartnershipData((p) => ({
      ...p,
      selectedPromotion: rateKey,
      promotionType: promoMeta.promotion,
      promotionPlatform: promoMeta.platform,
    }));

    if (!isCelebrity) {
      setDeliverableData((d) => ({ ...d, amount: recommended.toFixed(2) }));
    }
  }, [promotions, creator, isCelebrity]);

  // 2. Warn if offer < 50 % recommended (skip for celebrity creators)
  useEffect(() => {
    if (isCelebrity) return setOfferWarning('');
    const recPrice   = creator[partnershipData.selectedPromotion];
    const userAmount = parseFloat(deliverableData.amount || '0');
    if (!recPrice)   return setOfferWarning('');
    setOfferWarning(
      userAmount < recPrice * 0.5
        ? `Warning: This offer is below 50 % of the recommended $${recPrice}.`
        : '',
    );
  }, [deliverableData.amount, partnershipData.selectedPromotion, creator, isCelebrity]);

  /* ──────────────── HANDLERS ──────────────── */

  const handleDeliverableChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDeliverableData((d) => ({ ...d, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePartnershipChange = (e) => {
    const { name, value } = e.target;
    setPartnershipData((p) => ({ ...p, [name]: value }));
  };

  const handlePromotionChange = (rateKey) => {
    const meta        = getPlatformAndPromotion(rateKey);
    const recommended = creator[rateKey] ?? 50;

    setPartnershipData((p) => ({
      ...p,
      selectedPromotion: rateKey,
      promotionType: meta.promotion,
      promotionPlatform: meta.platform,
    }));

    // Only auto-fill for non-celebrities
    setDeliverableData((d) => ({
      ...d,
      amount: isCelebrity ? '' : recommended.toFixed(2),
    }));
  };

  /* ──────────────── SUBMIT ──────────────── */

  const { mutate: postPartnership, isLoading } = useMutation(API.partnerships.book, {
    onError: (err) =>
      openDialog(
        'Error',
        <>An error occurred while creating your partnership:<br />{err?.response?.data?.error || err.message}</>,
        closeDialog,
        closeDialog,
        'Ok',
      ),
    onSuccess: (data) => {
      if (currentToken) {
        openDialog(
          'Success',
          <>Booking created successfully. View it in your Blitz dashboard.</>,
          () => navigate(`/partnerships/${data.partnership.id}`),
          () => navigate(`/partnerships/${data.partnership.id}`),
          'Ok',
        );
        return;
      }
      const params = new URLSearchParams();
      params.set('tab', 'brand');
      if (deliverableData.email) params.set('email', deliverableData.email);
      navigate(`/onboard?${params.toString()}`);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    /* basic validation */
    if ((deliverableData.notes || '').trim().length < 10) {
      openDialog(
        'Validation Error',
        'Please provide a detailed brief/notes for your offer (at least 10 characters).',
        closeDialog,
        closeDialog,
        'Ok',
      );
      return;
    }

    if (!deliverableData.amount || parseFloat(deliverableData.amount) <= 0) {
      openDialog(
        'Validation Error',
        'Please enter a valid offer amount.',
        closeDialog,
        closeDialog,
        'Ok',
      );
      return;
    }

    const today   = new Date().toISOString().slice(0, 10);
    const payload = {
      creator: creator.display_name,
      name: partnershipData.partnershipName || `Offer for ${creator.display_name}`,
      description: partnershipData.details || deliverableData.notes || 'No Additional Info Provided',
      proposal_date: today,
      finish_date: today,
      total_sum: parseLocalFloat(deliverableData.amount),
      notes: deliverableData.notes,
      promotionType: partnershipData.promotionType,
      promotionPlatform: partnershipData.promotionPlatform,
      selectedPromotion: partnershipData.selectedPromotion,
      ...(!currentToken && { email: deliverableData.email }),

      deliverable: {
        deliverable_type: deliverableData.deliverableType,
        amount: parseLocalFloat(deliverableData.amount),
        notes: deliverableData.notes,
        platforms: [partnershipData.promotionPlatform],
      },
    };

    postPartnership(payload);
  };

  /* ──────────────── RENDER ──────────────── */

  return (
    <>
      <AlertDialog alertState={dialogState} />
      <Backdrop sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 1 }} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
        <Box sx={{ width: { xs: '100%', md: '30%' } }}>
          <CardMedia
            component="img"
            image={creator.pfphref || defaultPfp}
            alt={creator.display_name}
            sx={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', borderRadius: (t) => `${t.shape.borderRadius}px` }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, textTransform: 'capitalize', mb: 0.5 }}>
            {creator.display_name}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Partner with <b>{creator.display_name}</b> today!
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* form */}
      <Box component="form" onSubmit={handleSubmit}>
        {!!promotions.length && (
          <TextField
            select
            label="Collaboration Type"
            fullWidth
            margin="dense"
            name="selectedPromotion"
            value={partnershipData.selectedPromotion}
            onChange={(e) => handlePromotionChange(e.target.value)}
            required
            sx={{ mb: 2 }}
          >
            {promotions.map((p) => (
              <MenuItem key={p.rateKey} value={p.rateKey}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        {/* amount – blank by default for celebs */}
        <RateField
          label="Offer Amount"
          fullWidth
          name="amount"
          type="number"
          margin="dense"
          required
          value={deliverableData.amount}
          onChange={(val) => handleDeliverableChange({ target: { name: 'amount', value: val } })}
          sx={{ mb: 2 }}
          error={!!offerWarning}
          helperText={offerWarning}
        />

        {/* notes */}
        <TextField
          label="Offer Brief / Notes *"
          fullWidth
          multiline
          rows={3}
          name="notes"
          margin="dense"
          required
          value={deliverableData.notes}
          onChange={handleDeliverableChange}
          sx={{ mb: 2 }}
        />

        {!currentToken && (
          <TextField
            label="Email"
            fullWidth
            name="email"
            type="email"
            autoComplete="email"
            margin="dense"
            required
            value={deliverableData.email}
            onChange={handleDeliverableChange}
            sx={{ mb: 2 }}
          />
        )}

        <Button variant="outlined" onClick={() => setShowAdvanced((s) => !s)} sx={{ mb: 2 }}>
          {showAdvanced ? 'Hide Advanced Fields' : 'Advanced Collab Info'}
        </Button>

        {showAdvanced && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              Advanced Partnership Info
            </Typography>
            <TextField
              label="Partnership Name"
              fullWidth
              name="partnershipName"
              margin="dense"
              value={partnershipData.partnershipName}
              onChange={handlePartnershipChange}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Partnership Details"
              fullWidth
              multiline
              rows={3}
              name="details"
              margin="dense"
              value={partnershipData.details}
              onChange={handlePartnershipChange}
              sx={{ mb: 2 }}
            />
          </>
        )}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="contained" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" color="secondary" variant="contained" disabled={isLoading}>
            Submit
          </Button>
        </Box>
      </Box>
    </>
  );
}
